<template>
  <div class="tier-inline-form">
    <div class="tier-content">
      <v-row>
        <v-col cols="12" md="6">
          <b-text-field
            v-model="tierData.from"
            type="number"
            :color="tierData.color"
            min="0"
            :label="
              tiers_settings.package_mode === 'amount'
                ? translations.startingAmount[lang]
                : translations[`starting_${storeMode}`][lang]
            "
            dense
            :readonly="tierIndex === 0"
            :clearable="false"
            :success="success.minimum_amount && success.minimum_amount[tierIndex]"
            :error-messages="error.minimum_amount && error.minimum_amount[tierIndex]"
            @focus="clearLocalError('minimum_amount')"
            @blur="onFromBlur"
          ></b-text-field>
        </v-col>

        <v-col cols="12" md="6">
          <b-text-field
            :value="tierData.color"
            :label="translations.color[lang]"
            dense
            class="mb-4 color-picker-container"
            readonly
          >
            <template v-slot:prepend-inner class="mt-0">
              <span
                class="prepend-swatch my-0"
                :style="{ background: tierData.color || '#888888' }"
              ></span>
            </template>
            <template v-slot:append>
              <v-menu
                v-model="colorPickerOpen"
                :close-on-content-click="false"
                offset-y
                left
                nudge-bottom="8"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    small
                    depressed
                    color="secondary"
                    class="palette-btn"
                    v-on="on"
                    v-bind="attrs"
                  >
                    <v-icon>mdi-palette</v-icon>
                  </v-btn>
                </template>

                <v-card class="pa-3" elevation="8">
                  <v-color-picker
                    v-model="tempColor"
                    :canvas-height="180"
                    :width="400"
                    mode="hexa"
                    :hide-inputs="false"
                  ></v-color-picker>
                  <v-row class="mt-2" align="center">
                    <v-col cols="5" class="d-flex justify-end" style="gap: 8px">
                      <v-btn small text @click="onCancelColor">Cancel</v-btn>
                      <v-btn small depressed color="secondary" @click="onSaveColor">OK</v-btn>
                    </v-col>
                  </v-row>
                </v-card>
              </v-menu>
            </template>
          </b-text-field>
        </v-col>
      </v-row>

      <b-text-field
        v-model="tierData.title[titleLang]"
        :label="translations.title[lang]"
        class="mb-4"
        :success="success.title && success.title[tierIndex]"
        :error-messages="error.title && error.title[tierIndex]"
        @focus="clearLocalError('title')"
        @blur="onTitleBlur"
      >
        <template v-slot:append>
          <b-lang-menu v-model="titleLang" type="inner"></b-lang-menu>
        </template>
      </b-text-field>

      <v-textarea
        v-model="tierData.description[descLang]"
        :label="translations.description[lang]"
        outlined
        clearable
        no-resize
        rows="3"
        class="mb-4"
        :success="success.description && success.description[tierIndex]"
        :error-messages="error.description && error.description[tierIndex]"
        @focus="clearLocalError('description')"
        @blur="onDescriptionBlur"
      >
        <template v-slot:append>
          <b-lang-menu v-model="descLang" type="inner"></b-lang-menu>
        </template>
      </v-textarea>
      <v-row v-if="tiers_settings.system === 'expiration_mode'" class="mt-1">
        <v-col v-if="tierIndex > 0" cols="12" md="6" class="d-flex flex-column pt-0">
          <b-text-field
            v-model="tierData.expiration_days"
            type="number"
            :label="translations.expirationDays[lang]"
            no-margin-top
            :success="success.expiration_days && success.expiration_days[tierIndex]"
            :error-messages="error.expiration_days && error.expiration_days[tierIndex]"
            @focus="clearLocalError('expiration_days')"
            @blur="onExpirationBlur"
          ></b-text-field>
          <v-alert dense outlined type="warning" class="my-1 pb-2"
            ><span class="exp-days-description mb-n3">{{
              translations.expirationDaysDescription[lang]
            }}</span></v-alert
          >
        </v-col>
        <v-col v-if="tierIndex" cols="12" md="6" class="d-flex flex-column pt-0">
          <b-text-field
            v-model="tierData.valid_days_before"
            :label="translations.countableDays[lang]"
            no-margin-top
            :success="success.valid_days_before && success.valid_days_before[tierIndex]"
            :error-messages="error.valid_days_before && error.valid_days_before[tierIndex]"
            @focus="clearLocalError('valid_days_before')"
            @blur="onValidDaysBlur"
          ></b-text-field>
          <span class="exp-days-description">{{
            translations.countableDaysDescription[lang]
          }}</span>
        </v-col>
      </v-row>
    </div>
    <div class="tier-footer">
      <v-btn
        color="secondary"
        depressed
        class="text-capitalize"
        :disabled="!hasChanges || hasAnyErrors"
        @click="submitDialog"
        >{{ translations.applyChanges?.[lang] }}</v-btn
      >
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import translations from '@/utils/translations/loyaltyPanel/pointTitles';
import validators from '@/utils/validators/loyaltyPanel/pointTiers';
import { cloneDeep, isEqual } from 'lodash';

export default {
  name: 'PointTierForm',

  mixins: [translations, validators],

  props: {
    tier: Object,
    tierIndex: Number,
    infinityMultiplier: Number,
    tierValues: Array,
  },

  data() {
    return {
      icons: {},
      colorPickerOpen: false,
      tempColor: '',
      titleLang: this.$route.params.lang,
      descLang: this.$route.params.lang,
      changeTitleElAuto: true,
      changeTitleItAuto: true,
      changeDescriptionElAuto: true,
      changeDescriptionItAuto: true,
      changeTitleEnAuto: true,
      changeDescriptionEnAuto: true,
      tierData: this.tier ? cloneDeep(this.tier) : { title: {}, description: {} },
      originalTier: this.tier ? cloneDeep(this.tier) : { title: {}, description: {} },
    };
  },

  computed: {
    ...mapState(['loading', 'errorMessage']),
    ...mapState('loyaltyPanel', ['tiers_settings']),
    ...mapState('loyaltyPanel/pointTitles', ['tiers']),

    lang() {
      return this.$route.params.lang;
    },

    storeMode() {
      return this.$store.state.store?.display_mode || 'points';
    },

    hasChanges() {
      return !isEqual(this.tierData, this.originalTier);
    },

    hasAnyErrors() {
      const keys = ['expiration_days', 'valid_days_before', 'title', 'description'];
      return keys.some((k) => {
        const arr = this.error[k];
        return Array.isArray(arr) && arr[this.tierIndex] && String(arr[this.tierIndex]).trim();
      });
    },
  },

  watch: {
    colorPickerOpen(val) {
      if (val) {
        this.tempColor = this.tierData?.color || '#888888';
      }
    },
    ['tierData.color'](val, oldVal) {
      if (!val || !oldVal) return;
      let newVal = [...val];
      let l = val.length;
      let ol = oldVal.length;
      if (oldVal[ol - 1] !== 'F' && oldVal[ol - 2] !== 'F' && val[0] === '#' && l === 9) {
        newVal[l - 1] = 'F';
        newVal[l - 2] = 'F';
        this.tierData.color = newVal.join();
      }
    },

    ['tierData.title.en'](val) {
      if (this.lang == 'en') {
        if (this.changeTitleElAuto) {
          this.tierData.title.el = val;
        }

        if (this.changeTitleItAuto) {
          this.tierData.title.it = val;
        }
        this.changeNameEnAuto = false;
      } else if (val != this.tierData.title[this.lang]) {
        this.changeTitleEnAuto = false;
      }
    },

    ['tierData.title.el'](val) {
      if (this.lang == 'el') {
        if (this.changeTitleEnAuto) {
          this.tierData.title.en = val;
        }

        if (this.changeTitleItAuto) {
          this.tierData.title.it = val;
        }
        this.changeNameElAuto = false;
      } else if (val != this.tierData.title[this.lang]) {
        this.changeTitleElAuto = false;
      }
    },

    ['tierData.title.it'](val) {
      if (this.lang == 'it') {
        if (this.changeTitleEnAuto) {
          this.tierData.title.en = val;
        }

        if (this.changeTitleElAuto) {
          this.tierData.title.el = val;
        }
        this.changeTitleItAuto = false;
      } else if (val != this.tierData.title[this.lang]) {
        this.changeTitleItAuto = false;
      }
    },

    ['tierData.description.en'](val) {
      if (this.lang == 'en') {
        if (this.changeDescriptionElAuto) {
          this.tierData.description.el = val;
        }

        if (this.changeDescriptionItAuto) {
          this.tierData.description.it = val;
        }
        this.changeDescriptionEnAuto = false;
      } else if (val != this.tierData.description[this.lang]) {
        this.changeDescriptionEnAuto = false;
      }
    },

    ['tierData.description.el'](val) {
      if (this.lang == 'el') {
        if (this.changeDescriptionEnAuto) {
          this.tierData.description.en = val;
        }

        if (this.changeDescriptionItAuto) {
          this.tierData.description.it = val;
        }
        this.changeDescriptionElAuto = false;
      } else if (val != this.tierData.description[this.lang]) {
        this.changeDescriptionElAuto = false;
      }
    },

    ['tierData.description.it'](val) {
      if (this.lang == 'it') {
        if (this.changeDescriptionEnAuto) {
          this.tierData.description.en = val;
        }

        if (this.changeDescriptionElAuto) {
          this.tierData.description.el = val;
        }
        this.changeDescriptionItAuto = false;
      } else if (val != this.tierData.description[this.lang]) {
        this.changeDescriptionItAuto = false;
      }
    },

    ['$route.params.lang'](val) {
      this.titleLang = val;
      this.descLang = val;
    },

    tier: {
      immediate: true,
      handler(newTier) {
        if (!newTier) return;
        if (!this.hasChanges) {
          this.tierData = cloneDeep(newTier);
          this.originalTier = cloneDeep(newTier);
          this.validateTierForm(this.tierIndex, this.tierData);
        }
      },
    },
  },
  methods: {
    clearLocalError(type) {
      if (Array.isArray(this.error[type])) {
        this.$set(this.error[type], this.tierIndex, '');
        this.$set(this.success[type], this.tierIndex, false);
      }
    },

    onFromBlur() {
      this.validateMinimumAmount(this.tierIndex, this.tierData, false);
      this.$emit('from-blur', { i: this.tierIndex, tierData: this.tierData });
    },

    onTitleBlur() {
      this.validateTitle({ i: this.tierIndex, tierData: this.tierData });
    },

    onDescriptionBlur() {
      this.validateDescription({ i: this.tierIndex, tierData: this.tierData });
    },

    onExpirationBlur() {
      this.validateExpirationDays({ i: this.tierIndex, tierData: this.tierData });
    },

    onValidDaysBlur() {
      this.validateCountableDays({ i: this.tierIndex, tierData: this.tierData });
    },

    onSaveColor() {
      this.tierData.color = this.tempColor;
      this.colorPickerOpen = false;
    },

    onCancelColor() {
      this.colorPickerOpen = false;
    },

    getTextColor(hex) {
      const clean = (hex || '#888888').replace('#', '').slice(0, 6);
      const r = parseInt(clean.substring(0, 2), 16) / 255;
      const g = parseInt(clean.substring(2, 4), 16) / 255;
      const b = parseInt(clean.substring(4, 6), 16) / 255;
      const toLinear = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
      const L = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
      return L > 0.179 ? '#000000' : '#ffffff';
    },

    submitDialog() {
      this.$emit('submit', { tier: this.tierData, i: this.tierIndex });
    },

    resetForm() {
      if (!this.tier) return;
      this.tierData = cloneDeep(this.tier);
      this.originalTier = cloneDeep(this.tier);
      this.resetValidation && this.resetValidation();
      this.validateTierForm(this.tierIndex, this.tierData);
    },
  },

  mounted() {
    this.validateTierForm(this.tierIndex, this.tierData);
  },
};
</script>

<style lang="scss" scoped>
.tier-inline-form {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.tier-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

.color-picker-button {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    filter 0.2s,
    box-shadow 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
  flex-shrink: 0;

  &:hover {
    filter: brightness(1.1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16);
  }
}

.color-label {
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.7);
  margin-right: 8px;
  align-self: center;
}

.color-hex-field >>> .v-input__append-inner {
  display: flex;
  align-items: center;
}

.palette-btn {
  min-width: 84px;
  border-radius: 8px;
  text-transform: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding-left: 8px;
  padding-right: 10px;
}

.palette-swatch {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.palette-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.95);
}

.prepend-swatch {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-block;
  margin-left: 4px;
  margin-right: 4px;
}

.tier-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.tier-errors {
  padding: 0 16px;
  background: rgba(244, 67, 54, 0.02);
  flex-shrink: 0;
}

.tier-footer {
  padding: 12px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
}

.exp-days-description {
  padding: 3px 0 0 0;
  font-size: 0.82em;
  color: #666;
  font-style: italic;
}
</style>
